import React, { useState, useEffect } from 'react';
import {
  Box, VStack, Heading, Button, FormControl, FormLabel,
  Textarea, Select, useToast, SimpleGrid, Text,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { masterRiskApi, lookupApi } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';

const RiskForm: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { lang, t } = useLanguage();
  const [saving, setSaving] = useState(false);

  const [riskTypes, setRiskTypes] = useState<any[]>([]);
  const [strategies, setStrategies] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    risk_name: '',
    risk_type_id: 0,
    strategy_id: 0,
  });

  useEffect(() => {
    const fetchLookup = async () => {
      const [rtRes, stRes] = await Promise.allSettled([
        lookupApi.getRiskTypes(),
        lookupApi.getStrategies(),
      ]);
      if (rtRes.status === 'fulfilled') setRiskTypes(rtRes.value.data);
      if (stRes.status === 'fulfilled') setStrategies(stRes.value.data);
    };
    fetchLookup();
  }, []);

  const handleSave = async () => {
    if (!formData.risk_name || !formData.risk_type_id || !formData.strategy_id) {
      toast({ title: t('form.fillRequired'), status: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await masterRiskApi.create(formData);
      toast({ title: t('section.added'), status: 'success' });
      navigate('/risk-register');
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('form.saveError'), status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Heading size="lg" color="blue.600">{t('riskRegister.addMasterRisk')}</Heading>
        <Text color="gray.500" fontSize="sm">สร้างความเสี่ยงหลักใหม่ (Master Risk Concept) — ระบบจะสร้างรหัสความเสี่ยงอัตโนมัติจากประเภท + ยุทธศาสตร์</Text>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <VStack spacing={5} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('common.type')}</FormLabel>
                <Select value={formData.risk_type_id} onChange={(e) => setFormData({ ...formData, risk_type_id: Number(e.target.value) })}>
                  <option value={0}>{t('form.selectRiskType')}</option>
                  {riskTypes.map((rt) => (<option key={rt.id} value={rt.id}>{lang === 'en' ? rt.name_en : rt.name_th}</option>))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>{t('common.strategy')}</FormLabel>
                <Select value={formData.strategy_id} onChange={(e) => setFormData({ ...formData, strategy_id: Number(e.target.value) })}>
                  <option value={0}>{t('form.selectStrategy')}</option>
                  {strategies.map((s) => (<option key={s.id} value={s.id}>{lang === 'en' ? t(`strategy.${s.code}`) : s.name}</option>))}
                </Select>
              </FormControl>
            </SimpleGrid>

            <FormControl isRequired>
              <FormLabel>{t('common.riskName')}</FormLabel>
              <Textarea value={formData.risk_name} onChange={(e) => setFormData({ ...formData, risk_name: e.target.value })} rows={3} />
            </FormControl>

            <VStack spacing={3} align="stretch">
              <Button colorScheme="blue" size="lg" onClick={handleSave} isLoading={saving}>{t('common.save')}</Button>
              <Button variant="ghost" onClick={() => navigate('/risk-register')}>{t('common.cancel')}</Button>
            </VStack>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default RiskForm;
